import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Chart from "react-apexcharts";
import dashboardReportsApi from "../../api/dashboardReportsApi";
import Loading from "../../components/Loading";

const AccountReport = ({ accountId }) => {
  const token = useSelector((state) => state.auth.access_token);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    accountReport: null,
    taskStatus: null,
    taskClosures: [],
    metrics: null,
  });
  const fetchAccountData = async () => {
    try {
      setLoading(true);

      const [accountReport, taskStatus, taskClosures, metrics] =
        await Promise.all([
          dashboardReportsApi.getAccountReport(accountId, token),
          dashboardReportsApi.getAllTasksStatusById(accountId, token),
          dashboardReportsApi.getTasksClosuresByAccountId(accountId, token),
          dashboardReportsApi.getMetricsAccountById(accountId, token),
        ]);

      setData({
        accountReport,
        taskStatus,
        taskClosures: taskClosures || [],
        metrics,
      });
    } catch (err) {
      console.error("Failed to fetch account data:", err);
      setError("Failed to load account report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token && accountId) {
      fetchAccountData();
    }
  }, [token, accountId]);

  const formatDateTime = (date) => {
    if (!date) return "--";
    return new Intl.DateTimeFormat("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(date));
  };

  // Prepare chart data
  const statusSeries = [
    data.taskStatus?.totalTodo || 0,
    data.taskStatus?.totalInProgress || 0,
    data.taskStatus?.totalInReview || 0,
    data.taskStatus?.totalCompleted || 0,
    data.taskStatus?.totalOverdue || 0,
    data.taskStatus?.totalPending || 0,
  ];

  const hasStatusData = statusSeries.some((v) => v > 0);

  const statusChartOptions = {
    labels: [
      "Todo",
      "In Progress",
      "Review",
      "Completed",
      "Overdue",
      "Pending",
    ],
    legend: {
      position: "right",
      fontSize: "11px",
      itemMargin: {
        horizontal: 8,
        vertical: 2,
      },
    },
    plotOptions: {
      pie: {
        donut: {
          size: "55%",
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        return Math.round(val) + "%";
      },
      style: {
        fontSize: "10px",
        fontFamily: "Inter, sans-serif",
      },
    },
    colors: ["#6366f1", "#8b5cf6", "#06b6d4", "#10b981", "#f59e0b", "#ef4444"],
    responsive: [
      {
        breakpoint: 768,
        options: {
          chart: {
            height: 180,
          },
          legend: {
            position: "bottom",
            fontSize: "10px",
          },
        },
      },
    ],
  };

//   const performanceSeries = data.taskClosures.map(
//     (user) => user.tasksClosedThisMonth,
//   );
//   const performanceLabels = data.taskClosures.map((user) => user.username);

//   const performanceChartOptions = {
//     chart: {
//       type: "bar",
//       toolbar: { show: false },
//     },
//     plotOptions: {
//       bar: {
//         horizontal: true,
//         borderRadius: 4,
//         dataLabels: {
//           position: "top",
//         },
//       },
//     },
//     dataLabels: {
//       enabled: true,
//       offsetX: 0,
//       style: {
//         fontSize: "10px",
//         colors: ["#1f2937"],
//       },
//       formatter: function (val) {
//         return val;
//       },
//     },
//     xaxis: {
//       categories: performanceLabels,
//       labels: {
//         style: {
//           fontSize: "10px",
//           fontFamily: "Inter, sans-serif",
//         },
//       },
//       title: {
//         text: "Tasks Closed This Month",
//         style: {
//           fontSize: "11px",
//           fontWeight: 500,
//           fontFamily: "Inter, sans-serif",
//         },
//       },
//     },
//     yaxis: {
//       labels: {
//         style: {
//           fontSize: "10px",
//           fontFamily: "Inter, sans-serif",
//         },
//       },
//     },
//     colors: ["#4f46e5"],
//     tooltip: {
//       y: {
//         formatter: function (val) {
//           return val + " tasks";
//         },
//       },
//     },
//     responsive: [
//       {
//         breakpoint: 768,
//         options: {
//           chart: {
//             height: 180,
//           },
//           dataLabels: {
//             style: {
//               fontSize: "9px",
//             },
//           },
//         },
//       },
//     ],
//   };

  if (loading) return <Loading />;

  if (error)
    return (
      <div className="flex items-center justify-center overflow-hidden">
        <div className="text-center">
          <div className="text-red-500 text-lg font-medium mb-2">{error}</div>
          <button
            onClick={fetchAccountData}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );

  return (
    <div>
      <div className="grid lg:grid-cols-2 gap-4 mb-2">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 px-4 py-1 sm:p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-2">
            Account Metrics
          </h2>

          <div className="grid grid-cols-2 gap-2">
            <StatCard
              title="Total Accounts"
              value={data.metrics?.totalAccounts || 0}
              subtitle={`+${data.metrics?.accountsThisMonth || 0} this month`}
              color="indigo"
            />

            <StatCard
              title="Total Tasks"
              value={data.metrics?.totalTasks || 0}
              subtitle={`+${data.metrics?.tasksThisMonth || 0} this month`}
              color="blue"
            />

            <StatCard
              title="Total Workflows"
              value={data.metrics?.totalWorkflows || 0}
              subtitle={`+${data.metrics?.workflowsThisMonth || 0} this month`}
              color="emerald"
            />

            <StatCard
              title="Active Users"
              value={data.taskClosures?.length || 0}
              subtitle="Users with tasks"
              color="emerald"
            />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 px-4 py-1 sm:p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-2">
            Quick Stats
          </h2>
          <div className="grid grid-cols-2 gap-2">
            <StatCard
              title="Completion Rate"
              value={`${((((data.taskStatus?.totalCompleted || 0) + (data.taskStatus?.totalClosed || 0)) / (data.metrics?.totalTasks || 1)) * 100).toFixed(1)}%`}
              subtitle="Completed + Closed / Total Tasks"
              color="emerald"
            />
            <StatCard
              title="Overdue Rate"
              value={`${(((data.taskStatus?.totalOverdue || 0) / (data.metrics?.totalTasks || 1)) * 100).toFixed(1)}%`}
              subtitle="Overdue / Total Tasks"
              color="red"
            />
            <StatCard
              title="Monthly Growth"
              value={`+${data.metrics?.tasksThisMonth || 0}`}
              subtitle="New tasks this month"
              color="indigo"
            />
            <StatCard
              title="Active Tasks"
              value={
                (data.taskStatus?.totalInProgress || 0) +
                (data.taskStatus?.totalInReview || 0)
              }
              subtitle="In Progress + In Review"
              color="blue"
            />
          </div>
        </div>
      </div>

      <div className="lg:col-span-2 space-y-1 sm:space-y-1">
          <div className="grid lg:sm-cols-1 lg:grid-cols-3 gap-2">
            <div className="bg-white rounded-xl border border-slate-200 px-4 py-1">
              <h2 className="text-sm font-semibold text-slate-800 mb-2">
                Task Status Overview
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Total Tasks */}
                <div>
                  <div className="text-xs font-medium text-slate-500 mb-2">
                    Total Tasks
                  </div>
                  <div className="space-y-0.5">
                    <MetricRow
                      label="Todo"
                      value={data.taskStatus?.totalTodo}
                    />
                    <MetricRow
                      label="In Progress"
                      value={data.taskStatus?.totalInProgress}
                    />
                    <MetricRow
                      label="In Review"
                      value={data.taskStatus?.totalInReview}
                    />
                    <MetricRow
                      label="Completed"
                      value={data.taskStatus?.totalCompleted}
                      success
                    />
                    <MetricRow
                      label="Closed"
                      value={data.taskStatus?.totalClosed}
                      success
                    />
                    <MetricRow
                      label="Overdue"
                      value={data.taskStatus?.totalOverdue}
                      danger
                    />
                    <MetricRow
                      label="Pending"
                      value={data.taskStatus?.totalPending}
                    />
                    <MetricRow
                      label="Proposed"
                      value={data.taskStatus?.totalProposed}
                    />
                  </div>
                </div>

                {/* This Month */}
                <div>
                  <div className="text-xs font-medium text-slate-500 mb-2">
                    This Month
                  </div>
                  <div className="space-y-0.5">
                    <MetricRow
                      label="Todo"
                      value={data.taskStatus?.todoThisMonth}
                    />
                    <MetricRow
                      label="In Progress"
                      value={data.taskStatus?.inProgressThisMonth}
                    />
                    <MetricRow
                      label="In Review"
                      value={data.taskStatus?.inReviewThisMonth}
                    />
                    <MetricRow
                      label="Completed"
                      value={data.taskStatus?.completedThisMonth}
                      success
                    />
                    <MetricRow
                      label="Closed"
                      value={data.taskStatus?.closedThisMonth}
                      success
                    />
                    <MetricRow
                      label="Overdue"
                      value={data.taskStatus?.overdueThisMonth}
                      danger
                    />
                    <MetricRow
                      label="Pending"
                      value={data.taskStatus?.pendingThisMonth}
                    />
                    <MetricRow
                      label="Proposed"
                      value={data.taskStatus?.proposedThisMonth}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 px-4 py-1 sm:p-6">
              <h2 className="text-lg font-semibold text-slate-800 mb-4">
                Summary
              </h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">
                    Total Tasks Assigned
                  </span>
                  <span className="font-medium text-slate-800">
                    {data.metrics?.totalTasks || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Active Users</span>
                  <span className="font-medium text-slate-800">
                    {data.taskClosures.length}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">
                    Tasks This Month
                  </span>
                  <span className="font-medium text-indigo-600">
                    {data.metrics?.tasksThisMonth || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Last Updated</span>
                  <span className="text-sm text-slate-500">
                    {formatDateTime(
                      data.accountReport?.organizationMetrics?.lastRefreshedAt,
                    )}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-1 sm:space-y-2 ">
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 px-4 py-2 sm:px-6">
                <h2 className="text-lg font-semibold text-slate-800 mb-2">
                  Task Distribution
                </h2>
                {hasStatusData ? (
                  <Chart
                    options={statusChartOptions}
                    series={statusSeries}
                    type="donut"
                    height={180}
                  />
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    No task data available
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4 pb-2 border-b border-slate-200">
              User Performance
            </h2>
            {data.taskClosures.length > 0 ? (
              <div className="space-y-4">
                <div className="border-b border-slate-200 pb-4">
                  <h3 className="text-sm font-medium text-slate-600 mb-3">
                    Monthly Performance
                  </h3>
                  <Chart
                    options={performanceChartOptions}
                    series={[{ data: performanceSeries }]}
                    type="bar"
                    height={
                      performanceLabels.length > 3
                        ? performanceLabels.length * 40
                        : 180
                    }
                  />
                </div>

                <div>
                  <h3 className="text-sm font-medium text-slate-600 mb-3">
                    Detailed Performance
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-2 font-medium text-slate-600">
                            User
                          </th>
                          <th className="text-left py-2 font-medium text-slate-600">
                            Email
                          </th>
                          <th className="text-right py-2 font-medium text-slate-600">
                            This Month
                          </th>
                          <th className="text-right py-2 font-medium text-slate-600">
                            Total Closed
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.taskClosures.map((user, idx) => (
                          <tr
                            key={idx}
                            className="border-b border-slate-100 hover:bg-slate-50"
                          >
                            <td className="py-3">
                              <div className="font-medium text-slate-800">
                                {user.username}
                              </div>
                            </td>
                            <td className="py-3 text-slate-600">
                              {user.userEmail}
                            </td>
                            <td className="py-3 text-right">
                              <span className="font-bold text-indigo-600">
                                {user.tasksClosedThisMonth}
                              </span>
                            </td>
                            <td className="py-3 text-right font-medium">
                              {user.totalTasksClosed}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                No user performance data available
              </div>
            )}
          </div> */}
      </div>
    </div>
  );
};

const MetricRow = ({ label, value, success, danger }) => {
  let valueColor = "text-slate-800";

  if (success) valueColor = "text-emerald-600";
  if (danger) valueColor = "text-red-600";

  return (
    <div className="flex justify-between items-center text-xs py-0.5">
      <span className="text-slate-600">{label}</span>
      <span className={`font-medium ${valueColor}`}>{value ?? 0}</span>
    </div>
  );
};

const StatCard = ({ title, value, subtitle, color = "indigo" }) => {
  const colorClasses = {
    indigo: "bg-indigo-50 text-indigo-700",
    emerald: "bg-emerald-50 text-emerald-700",
    red: "bg-red-50 text-red-700",
    blue: "bg-blue-50 text-blue-700",
    amber: "bg-amber-50 text-amber-700",
  };

  return (
    <div className={`${colorClasses[color]} rounded-lg px-4 py-1`}>
      <div className="text-sm font-medium mb-1">{title}</div>
      <div className="text-2xl font-bold mb-1">{value}</div>
      <div className="text-xs opacity-80">{subtitle}</div>
    </div>
  );
};

export default AccountReport;
